import React from 'react';
import { Button } from '@/components/ui/button';
import { DIGITAL_INVITES_CATALOG_IDS, DIGITAL_INVITES_CATALOG_THEMES, catalogModelHasDemo } from '@/lib/digitalInvitesCatalog';
import { getDigitalInvitesCatalogPriceDisplay } from '@/lib/digitalInvitesPricing';
import { getDigitalInviteOrderWhatsappUrl, getDigitalInviteViewModelWhatsappUrl } from '@/lib/digitalInvitesWhatsapp';
import { DigitalInvitesCatalogPreview } from './DigitalInvitesCatalogPreview';
import { DigitalInvitesPriceBadges } from './DigitalInvitesPriceBadges';

function CatalogProductBlock({ model, copy, language, reverse }) {
  const modelName = `${model.titleUpper} ${model.titleScript}`.trim();
  const pricing = getDigitalInvitesCatalogPriceDisplay(model.id, language);
  const showDemo = catalogModelHasDemo(model.id) && model.hasDemo !== false;

  const openOrder = () => {
    window.open(getDigitalInviteOrderWhatsappUrl(modelName), '_blank', 'noopener,noreferrer');
  };

  const openDemo = () => {
    window.open(getDigitalInviteViewModelWhatsappUrl(modelName), '_blank', 'noopener,noreferrer');
  };

  const mockup = (
    <div className="flex justify-center">
      <DigitalInvitesCatalogPreview
        modelId={model.id}
        theme={DIGITAL_INVITES_CATALOG_THEMES[model.id]}
        themeLabel={model.themeLabel}
        showPlay={model.showPlay}
        showMusic={model.showMusic}
      />
    </div>
  );

  const pricingBlock = (
    <DigitalInvitesPriceBadges
      fromLabel={copy.priceFromLabel}
      primary={pricing.primary}
      secondary={pricing.secondary}
      customLabel={pricing.custom ? copy.planCustomPriceLabel : null}
    />
  );

  const content = (
    <div className="flex flex-col justify-center min-w-0">
      <h3 className="text-white font-black text-lg sm:text-xl tracking-[0.08em] uppercase leading-tight break-words">
        {model.titleUpper}
      </h3>
      <p className="text-amber-200/90 text-xl sm:text-2xl lg:text-[1.75rem] italic font-light -mt-0.5 mb-4 break-words">
        {model.titleScript}
      </p>
      <p className="text-white/55 text-sm sm:text-base font-light leading-relaxed mb-4 break-words">{model.desc}</p>
      <p className="text-xs sm:text-sm text-white/40 mb-5">
        <span className="font-semibold text-amber-200/70">{copy.formatLabel}:</span> {model.format}
      </p>
      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
        <Button
          type="button"
          onClick={openOrder}
          className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-semibold shadow-lg shadow-amber-900/25 hover:from-amber-400 hover:to-amber-500 h-11 px-6"
        >
          {copy.btnOrderNow}
        </Button>
        {showDemo ? (
          <Button
            type="button"
            variant="outline"
            onClick={openDemo}
            className="rounded-xl border-white/15 bg-white/[0.04] text-white hover:bg-white/10 hover:border-amber-500/30 h-11 px-6 font-semibold"
          >
            {copy.btnViewModel}
          </Button>
        ) : null}
      </div>
    </div>
  );

  return (
    <article className="group relative overflow-hidden rounded-[1.75rem] border border-white/[0.1] bg-slate-950/45 backdrop-blur-xl transition-all duration-300 hover:border-amber-500/25 hover:shadow-[0_28px_90px_-36px_rgba(245,158,11,0.18)]">
      <div
        className="absolute inset-0 bg-gradient-to-br from-amber-500/[0.05] via-transparent to-violet-500/[0.04] pointer-events-none"
        aria-hidden
      />
      <div
        className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-amber-500/[0.06] blur-3xl pointer-events-none group-hover:bg-amber-500/[0.09] transition-colors duration-500"
        aria-hidden
      />
      <div className="relative p-6 sm:p-8 lg:p-10">
        <div
          className={`grid gap-8 lg:gap-10 items-center ${
            reverse ? 'lg:grid-cols-[1fr_auto_auto]' : 'lg:grid-cols-[auto_auto_1fr]'
          }`}
        >
          {reverse ? (
            <>
              <div className="order-3 lg:order-1">{content}</div>
              <div className="order-2 flex justify-center">{pricingBlock}</div>
              <div className="order-1 lg:order-3">{mockup}</div>
            </>
          ) : (
            <>
              {mockup}
              <div className="flex justify-center">{pricingBlock}</div>
              {content}
            </>
          )}
        </div>
      </div>
    </article>
  );
}

export function DigitalInvitesCatalogSection({ copy, language }) {
  const models = copy.catalogModels.filter((m) => DIGITAL_INVITES_CATALOG_IDS.includes(m.id));

  return (
    <section id="digital-invites-gallery" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0 space-y-8 sm:space-y-10">
      <div className="text-center mb-4 sm:mb-6">
        <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight break-words">{copy.sectionCatalogTitle}</h2>
        <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light leading-relaxed break-words">{copy.sectionCatalogLead}</p>
        <div className="w-20 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto rounded-full mt-6" />
      </div>

      {models.map((model, index) => (
        <CatalogProductBlock key={model.id} model={model} copy={copy} language={language} reverse={index % 2 === 1} />
      ))}
    </section>
  );
}
